/**
 * L'argent de la démo — les ventes du compte de tournage, et ce qu'elles lui
 * rapportent.
 *
 * Kayla, 07-10-2026 : « comme s'il avait fait des ventes, comme s'il avait de
 * l'argent en attente et de l'argent prêt à retirer ». La vidéo de formation
 * montre au chatteur ce qu'il va GAGNER — un Personnel vide ne le montre pas.
 *
 * ⛔ Tout est FICTIF, et seulement en démo. Aucune vente, aucun versement n'est
 * écrit en base : de vraies lignes sur le compte Mayumi fausseraient les
 * statistiques et la paie, et un solde « prêt à retirer » réel pourrait partir
 * en USDT. Ici, `POST /api/payroll/withdraw` est servi par la démo (cf.
 * `serveur.ts`) — un clic sur « Retirer » ne quitte jamais le navigateur.
 *
 * UNE source pour tous les écrans : l'historique de ventes ci-dessous. Les
 * courbes d'Analytics, « Mes ventes », le solde disponible et le solde en
 * attente en DÉRIVENT — sinon le Portefeuille et Analytics diraient deux
 * montants différents à l'image.
 */
import type { MesVentesResponse, VenteBrute } from "@/panels/personnel/mesVentes";
import type { ReponseAnalytique } from "@/panels/personnel/analytics/calcul";
import type { MyTimeseriesResponse, TimeseriesBucket } from "@/panels/personnel/types";
import { payableWindow } from "@/panels/organisation/payoutCycle";
import { commissionFromGrossCents } from "@/shared/commission";

/** Le % de commission du compte de démo — celui d'un chatteur ordinaire. */
export const POURCENT_DE_DEMO = 15;

const MINUTE = 60_000;
const JOUR = 24 * 60 * MINUTE;

/** L'échelle de Kayla — ce qu'un chatteur vend vraiment (en dollars). */
const PRIX = [50, 70, 50, 100, 50, 70, 150, 50, 100, 70];
const PRODUITS: Record<number, string> = {
  50: "Vidéo rien que pour toi",
  70: "Vidéo · deuxième marche",
  100: "Vidéo longue",
  150: "Pack soirée",
};
const FANS = [
  "Lucas", "Karim", "Connor", "Jonas", "Maxime", "Luca", "Hugo", "Javier",
  "Quentin", "Ethan", "Romain", "Bilal", "Matteo", "Tristan", "Liam",
];
const CREATRICES = ["Trista", "Lilou", "Justine"];

/** Un pseudo-aléa FIXE : la même démo à chaque prise. */
function graine(n: number): number {
  const x = Math.sin(n * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

/** Minute murale de Paris d'un instant. */
function minuteDeParis(ms: number): number {
  const parts = new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(ms));
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return h * 60 + m;
}

/** La grille de change du backend (`sales_currency.CURRENCY_TO_USD`). */
const TAUX_USD: Record<string, number> = { USD: 1, EUR: 1.08 };

/** Ce que le fan a payé, et ce que ça fait en dollars — comme le backend. */
function paye(prix: number, devise: "EUR" | "USD") {
  const taux = TAUX_USD[devise]!;
  return {
    amount_cents: prix * 100,
    currency: devise,
    taux_usd: taux,
    gross_usd_cents: Math.round(prix * 100 * taux),
  };
}

let ventes: VenteBrute[] | null = null;

/**
 * 52 ventes sur les 60 derniers jours, plus denses les dernières semaines —
 * un chatteur qui progresse. Le soir surtout (18 h – 1 h), comme au poste.
 */
export function lesVentes(maintenant = Date.now()): VenteBrute[] {
  if (ventes) return ventes;
  const out: VenteBrute[] = [];
  for (let i = 0; i < 52; i++) {
    // Plus récentes = plus serrées : la racine tasse les ventes vers aujourd'hui.
    const ilYaJours = Math.floor(60 * Math.pow(graine(i), 1.6));
    const heure = 18 + Math.floor(graine(i + 100) * 7); // 18 h → 0 h
    const minute = Math.floor(graine(i + 200) * 60);
    const jourMs = maintenant - ilYaJours * JOUR;
    const d = new Date(jourMs);
    d.setHours(heure % 24, minute, 0, 0);
    let ms = d.getTime();
    if (ms > maintenant) ms -= JOUR;
    const prix = PRIX[i % PRIX.length]!;
    // Un fan sur cinq paie en dollars (les anglophones) ; les autres en euros.
    const devise = i % 5 === 2 ? "USD" : "EUR";
    out.push({
      order_id: `demo-order-${i}`,
      created_at: new Date(ms).toISOString(),
      paris_minute: minuteDeParis(ms),
      ...paye(prix, devise),
      product: PRODUITS[prix] ?? "Vidéo",
      fan_name: FANS[i % FANS.length]!,
      fan_username: null,
      creator_name: CREATRICES[i % CREATRICES.length]!,
    });
  }
  // Et deux ventes de CE soir : le Portefeuille a toujours un « en attente ».
  for (const [k, prix] of [[0, 50], [1, 70]] as const) {
    const ms = maintenant - (45 + 70 * k) * MINUTE;
    out.push({
      order_id: `demo-order-soir-${k}`,
      created_at: new Date(ms).toISOString(),
      paris_minute: minuteDeParis(ms),
      ...paye(prix, "EUR"),
      product: PRODUITS[prix] ?? "Vidéo",
      fan_name: k === 0 ? "Marco" : "Kevin",
      fan_username: null,
      creator_name: "Trista",
    });
  }
  ventes = out.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  return ventes;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Les périodes                                                               */
/* ────────────────────────────────────────────────────────────────────────── */

type Periode = "24h" | "7d" | "30d" | "1y" | "all";

const DEBUT_DE: Record<Periode, number | null> = {
  "24h": JOUR,
  "7d": 7 * JOUR,
  "30d": 30 * JOUR,
  "1y": 365 * JOUR,
  all: null,
};

function ventesDe(periode: string, maintenant: number): VenteBrute[] {
  const fenetre = DEBUT_DE[(periode as Periode) in DEBUT_DE ? (periode as Periode) : "30d"];
  return lesVentes(maintenant).filter(
    (v) => fenetre === null || Date.parse(v.created_at) >= maintenant - fenetre,
  );
}

/** `GET /api/sales-attribution/me/sales?period=…` */
export function mesVentes(periode: string, maintenant = Date.now()): MesVentesResponse {
  return { period: periode, sales: ventesDe(periode, maintenant) };
}

/** `GET /api/sales-attribution/me/timeseries?period=…` */
export function maCourbe(periode: string, maintenant = Date.now()): MyTimeseriesResponse {
  const p: Periode = periode in DEBUT_DE ? (periode as Periode) : "30d";
  const unite: MyTimeseriesResponse["bucket_unit"] =
    p === "24h" ? "hour" : p === "7d" || p === "30d" ? "day" : "month";
  const pas = unite === "hour" ? 3_600_000 : unite === "day" ? JOUR : 30 * JOUR;
  const nombre = p === "24h" ? 24 : p === "7d" ? 7 : p === "30d" ? 30 : 12;
  const debut = maintenant - nombre * pas;

  const buckets: TimeseriesBucket[] = Array.from({ length: nombre }, (_, i) => ({
    ts: new Date(debut + i * pas).toISOString(),
    messages_sent: 0,
    sales_count: 0,
    gross_revenue_cents: 0,
    gross_by_half_hour: new Array<number>(48).fill(0),
    commission_due_cents: 0,
    decisions: 0,
  }));
  for (const v of lesVentes(maintenant)) {
    const t = Date.parse(v.created_at);
    if (t < debut) continue;
    const b = buckets[Math.min(nombre - 1, Math.floor((t - debut) / pas))]!;
    b.sales_count += 1;
    b.gross_revenue_cents += v.gross_usd_cents;
    b.gross_by_half_hour![Math.floor(v.paris_minute / 30)]! += v.gross_usd_cents;
  }
  // Des messages tous les jours de shift, plus les jours où ça vend.
  buckets.forEach((b, i) => {
    const base = unite === "hour" ? (i >= 17 || i <= 1 ? 9 : 0) : unite === "day" ? 60 : 1_700;
    b.messages_sent = Math.round(base * (0.7 + graine(i + 300) * 0.6) + b.sales_count * 14);
  });

  const total = (k: "messages_sent" | "sales_count" | "gross_revenue_cents") =>
    buckets.reduce((s, b) => s + b[k], 0);
  const ventes = total("sales_count");
  return {
    workos_user_id: "demo",
    period: p,
    bucket_unit: unite,
    period_from: new Date(debut).toISOString(),
    period_to: new Date(maintenant).toISOString(),
    buckets,
    total_messages_sent: total("messages_sent"),
    total_sales_count: ventes,
    total_gross_revenue_cents: total("gross_revenue_cents"),
    total_commission_due_cents: 0,
    avg_sale_cents: ventes ? Math.round(total("gross_revenue_cents") / ventes) : 0,
    total_decisions: 0,
  };
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Le portefeuille                                                            */
/* ────────────────────────────────────────────────────────────────────────── */

export interface VersementDeDemo {
  _id: string;
  amount: number;
  currency: string;
  paidAt: number;
  periodStart: number;
  periodEnd: number;
  invoiceNumber: string | null;
  txAddress: string | null;
  note: string | null;
  markedByName: string | null;
  markedByEmail: string | null;
}

export interface Portefeuille {
  disponibleCents: number;
  enAttenteCents: number;
  versements: VersementDeDemo[];
}

let retire: VersementDeDemo[] = [];
const abonnes = new Set<() => void>();
let instantane: Portefeuille | null = null;

/**
 * Ce que le compte a gagné, dérivé des ventes : la commission débloquée
 * (vendue avant le dernier lundi midi) moins ce qui a déjà été versé, et la
 * commission en attente (vendue depuis). Deux versements passés en couvrent
 * environ les deux tiers — il reste de quoi retirer à l'image.
 */
export function lePortefeuille(maintenant = Date.now()): Portefeuille {
  if (instantane) return instantane;
  const deblocage = Date.parse(payableWindow(maintenant).to);
  const toutes = lesVentes(maintenant);
  const brut = (f: (t: number) => boolean) =>
    toutes.filter((v) => f(Date.parse(v.created_at))).reduce((s, v) => s + v.gross_usd_cents, 0);
  const gagne = commissionFromGrossCents(brut((t) => t < deblocage), POURCENT_DE_DEMO) ?? 0;
  const enAttente = commissionFromGrossCents(brut((t) => t >= deblocage), POURCENT_DE_DEMO) ?? 0;

  const premier = Math.round(gagne * 0.3);
  const second = Math.round(gagne * 0.35);
  const passes: VersementDeDemo[] = [
    versement("demo-versement-2", second, deblocage - 7 * JOUR + 6 * 3_600_000, deblocage - 14 * JOUR, deblocage - 7 * JOUR),
    versement("demo-versement-1", premier, deblocage - 14 * JOUR + 5 * 3_600_000, deblocage - 21 * JOUR, deblocage - 14 * JOUR),
  ];
  const deja = [...retire, ...passes];
  const verse = deja.reduce((s, p) => s + p.amount, 0);
  instantane = {
    disponibleCents: Math.max(0, gagne - verse),
    enAttenteCents: enAttente,
    versements: deja,
  };
  return instantane;
}

function versement(
  id: string,
  cents: number,
  paidAt: number,
  periodStart: number,
  periodEnd: number,
): VersementDeDemo {
  return {
    _id: id,
    amount: cents,
    currency: "USDT",
    paidAt,
    periodStart,
    periodEnd,
    invoiceNumber: null,
    txAddress: null,
    note: "Retrait USDT",
    markedByName: null,
    markedByEmail: null,
  };
}

/**
 * `POST /api/payroll/withdraw` — le retrait, joué ici. Il rend ce que le vrai
 * serveur rend ; le solde passe à zéro et le versement entre dans la liste
 * quelques secondes plus tard, le temps que le bouton montre « Retrait
 * envoyé » à l'image.
 */
export function retirer(maintenant = Date.now()): {
  status: "success";
  amountCents: number;
  txAddress: string;
  solscanUrl: string;
} {
  const montant = lePortefeuille(maintenant).disponibleCents;
  setTimeout(() => {
    retire = [
      { ...versement(`demo-retrait-${retire.length}`, montant, Date.now(), Date.now() - 7 * JOUR, Date.now()) },
      ...retire,
    ];
    instantane = null;
    for (const f of [...abonnes]) f();
  }, 4_000);
  return {
    status: "success",
    amountCents: montant,
    txAddress: "démo — aucun envoi",
    solscanUrl: "https://solscan.io",
  };
}

/* ────────────────────────────────────────────────────────────────────────── */
/* La vente jouée pendant la prise                                            */
/* ────────────────────────────────────────────────────────────────────────── */

const ecouteursDeVente = new Set<(vente: VenteBrute) => void>();

/** S'abonne aux ventes de la prise (la fête, le classement) ; rend le
 *  désabonnement. */
export function abonnerAuxVentes(f: (vente: VenteBrute) => void): () => void {
  ecouteursDeVente.add(f);
  return () => {
    ecouteursDeVente.delete(f);
  };
}

/**
 * Le fan fictif vient de payer : la vente entre dans l'historique, d'où le
 * Portefeuille, le classement et la fête la lisent — UNE source, comme le
 * reste de cet argent. Kayla, 15-10-2026 : la fête ne partait pas au paiement
 * du fan de démo, et le classement montrait les vrais chatteurs.
 */
export function enregistrerUneVente(
  vente: { cents: number; devise: string; fan: string | null; creatrice: string | null; produit: string | null },
  maintenant = Date.now(),
): VenteBrute {
  const taux = TAUX_USD[vente.devise] ?? 1;
  const brute: VenteBrute = {
    order_id: `demo-order-prise-${maintenant}`,
    created_at: new Date(maintenant).toISOString(),
    paris_minute: minuteDeParis(maintenant),
    amount_cents: vente.cents,
    currency: vente.devise,
    taux_usd: taux,
    gross_usd_cents: Math.round(vente.cents * taux),
    product: vente.produit,
    fan_name: vente.fan,
    fan_username: null,
    creator_name: vente.creatrice,
  };
  ventes = [brute, ...lesVentes(maintenant)];
  instantane = null;
  for (const f of [...abonnes]) f();
  for (const f of [...ecouteursDeVente]) {
    try {
      f(brute);
    } catch (err) {
      console.error("[démo] un abonné des ventes a levé", err);
    }
  }
  return brute;
}

export function abonnerAuPortefeuille(f: () => void): () => void {
  abonnes.add(f);
  return () => {
    abonnes.delete(f);
  };
}

/** Repart de zéro (nouveau monde, et le banc). */
export function oublierLargent(): void {
  ventes = null;
  retire = [];
  instantane = null;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Mon shift                                                                  */
/* ────────────────────────────────────────────────────────────────────────── */

const QUART = 15 * MINUTE;

/**
 * Les shifts du compte de démo : celui en cours (commencé il y a 2 h 30, pour
 * que la courbe ait déjà de quoi monter à l'image) et les trois soirs d'avant.
 */
export function lesShiftsDeDemo(maintenant = Date.now()): Array<{ id: string; from: number; to: number | null }> {
  const enCours = Math.floor((maintenant - 150 * MINUTE) / QUART) * QUART;
  const soirs = [1, 2, 3].map((j) => {
    const d = new Date(maintenant - j * JOUR);
    d.setHours(18, 0, 0, 0);
    return { id: `demo-shift-${j}`, from: d.getTime(), to: d.getTime() + 5 * 3_600_000 };
  });
  return [{ id: "demo-shift-en-cours", from: enCours, to: null }, ...soirs];
}

/** `GET /api/sales-attribution/me/shift?from=&to=` — dérivé des MÊMES ventes. */
export function monShift(fromIso: string, toIso: string, maintenant = Date.now()) {
  const debut = Date.parse(fromIso);
  const fin = Math.max(debut, Math.min(Date.parse(toIso), maintenant));
  const n = Math.max(1, Math.ceil((fin - debut) / QUART));
  const lignes = lesVentes(maintenant).filter((v) => {
    const t = Date.parse(v.created_at);
    return t >= debut && t < fin;
  });
  const buckets = Array.from({ length: n }, (_, i) => ({
    ts: new Date(debut + i * QUART).toISOString(),
    // Un chatteur écrit tout le shift : 6 à 14 messages par quart d'heure.
    messages_sent: 6 + Math.floor(graine(Math.floor((debut + i * QUART) / QUART)) * 9),
    offers: 0,
    sales: 0,
    gross_usd_cents: 0,
  }));
  const seau = (t: number) => buckets[Math.min(n - 1, Math.floor((t - debut) / QUART))]!;
  for (const v of lignes) {
    const b = seau(Date.parse(v.created_at));
    b.sales += 1;
    b.offers += 1;
    b.gross_usd_cents += v.gross_usd_cents;
  }
  // Des offres qui n'ont pas (encore) payé : à peu près une de plus pour deux ventes.
  const offresEnPlus = Math.max(1, Math.round(lignes.length * 0.6));
  for (let k = 0; k < offresEnPlus; k++) buckets[Math.floor(graine(k + debut / QUART) * n)]!.offers += 1;

  const messages = buckets.reduce((s, b) => s + b.messages_sent, 0);
  const offres = buckets.reduce((s, b) => s + b.offers, 0);
  const conversations = Math.max(lignes.length + offresEnPlus, Math.round(messages / 7));
  return {
    workos_user_id: "demo",
    window_from: new Date(debut).toISOString(),
    window_to: new Date(fin).toISOString(),
    messages_sent: messages,
    conversations,
    offers: offres,
    sales: lignes.length,
    gross_usd_cents: lignes.reduce((s, v) => s + v.gross_usd_cents, 0),
    fans_converted: new Set(lignes.map((v) => v.fan_name)).size,
    fans_offered: Math.min(conversations, new Set(lignes.map((v) => v.fan_name)).size + offresEnPlus),
    buckets,
    sales_rows: lignes,
  };
}

/** `GET /api/sales-attribution/me/analytique?period=7d|30d` — dérivé des MÊMES
 *  ventes ; les messages et les offres sont fabriqués comme pour « Mon shift ». */
export function monAnalytique(periode: string, maintenant = Date.now()): ReponseAnalytique {
  if (periode === "1y" || periode === "all") return auMois(monAnalytique("60j", maintenant), periode, maintenant);
  const jours = periode === "30d" ? 30 : periode === "60j" ? 60 : 7;
  const debut = maintenant - jours * JOUR;
  const lignes = lesVentes(maintenant).filter((v) => Date.parse(v.created_at) >= debut);
  const parJour = new Map<string, { messages_sent: number; conversations: number; offers: number; sales: number; gross_usd_cents: number; fans: Set<string> }>();
  for (let k = jours; k >= 0; k--) {
    const d = new Date(maintenant - k * JOUR).toISOString().slice(0, 10);
    // Un jour sur sept sans shift ; sinon 60 à 140 messages.
    const repos = graine(k + 900) < 0.14;
    const messages = repos ? 0 : 60 + Math.floor(graine(k + 700) * 80);
    parJour.set(d, { messages_sent: messages, conversations: Math.round(messages / 7), offers: 0, sales: 0, gross_usd_cents: 0, fans: new Set() });
  }
  const heures = Array.from({ length: 24 }, (_, h) => ({
    heure: h,
    messages_sent: 0,
    offers: 0,
    sales: 0,
    gross_usd_cents: 0,
  }));
  for (const v of lignes) {
    const j = parJour.get(v.created_at.slice(0, 10));
    if (j) {
      j.sales += 1;
      j.offers += 2;
      j.gross_usd_cents += v.gross_usd_cents;
      j.fans.add(v.fan_name ?? v.order_id);
      if (j.messages_sent === 0) {
        j.messages_sent = 70;
        j.conversations = 10;
      }
    }
    const h = heures[Math.floor(v.paris_minute / 60)]!;
    h.sales += 1;
    h.offers += 2;
    h.gross_usd_cents += v.gross_usd_cents;
  }
  // Les messages SUIVENT les ventes : on écrit autour des heures où l'on
  // vend (sinon le graphe montrerait des ventes à des heures sans un message,
  // ce qu'aucun chatteur ne fait). Plus une heure vend, plus elle a été
  // travaillée — mais pas proportionnellement : c'est l'écart qui se lit.
  heures.forEach((h, i) => {
    const voisines = heures[(i + 23) % 24]!.sales + heures[(i + 1) % 24]!.sales;
    if (h.sales + voisines === 0) return;
    h.messages_sent = Math.round(jours * (8 + graine(i + 50) * 10) + h.sales * 18 + voisines * 9);
  });
  const liste = [...parJour.entries()].map(([jour, j]) => ({
    jour,
    messages_sent: j.messages_sent,
    conversations: j.conversations,
    offers: j.offers,
    sales: j.sales,
    gross_usd_cents: j.gross_usd_cents,
    fans_converted: j.fans.size,
  }));
  const somme = (k: "messages_sent" | "conversations" | "offers" | "sales" | "gross_usd_cents" | "fans_converted") =>
    liste.reduce((s, j) => s + j[k], 0);
  return {
    workos_user_id: "demo",
    period: periode,
    window_from: new Date(debut).toISOString(),
    window_to: new Date(maintenant).toISOString(),
    messages_sent: somme("messages_sent"),
    conversations: somme("conversations"),
    offers: somme("offers"),
    sales: somme("sales"),
    gross_usd_cents: somme("gross_usd_cents"),
    fans_converted: somme("fans_converted"),
    fans_offered: Math.min(somme("conversations"), Math.round(somme("offers") * 0.8)),
    jours: liste,
    heures,
  };
}

/** 1 an et tout, en démo : les mêmes jours, regroupés par MOIS (la démo vend
 *  depuis 60 jours — « tout » commence donc il y a deux mois). */
function auMois(r: ReponseAnalytique, periode: string, maintenant: number): ReponseAnalytique {
  const mois = new Map<string, ReponseAnalytique["jours"][number]>();
  const debut = periode === "1y" ? new Date(maintenant - 365 * JOUR) : new Date(r.window_from);
  for (let d = new Date(Date.UTC(debut.getUTCFullYear(), debut.getUTCMonth(), 1)); d.getTime() <= maintenant; d.setUTCMonth(d.getUTCMonth() + 1)) {
    const k = d.toISOString().slice(0, 10);
    mois.set(k, { jour: k, messages_sent: 0, conversations: 0, offers: 0, sales: 0, gross_usd_cents: 0, fans_converted: 0 });
  }
  for (const j of r.jours) {
    const m = mois.get(`${j.jour.slice(0, 7)}-01`);
    if (!m) continue;
    m.messages_sent += j.messages_sent;
    m.conversations += j.conversations;
    m.offers += j.offers;
    m.sales += j.sales;
    m.gross_usd_cents += j.gross_usd_cents;
    m.fans_converted += j.fans_converted;
  }
  return {
    ...r,
    period: periode,
    unite: "mois" as const,
    window_from: debut.toISOString(),
    jours: [...mois.values()],
  };
}
